import HelpOutlineIcon from "@mui/icons-material/HelpOutline"
import PrintIcon from "@mui/icons-material/Print"
import { CircularProgress, IconButton, Stack, Tooltip } from "@mui/material"
import { FC, useState } from "react"
import { useTranslation } from "react-i18next"
import { Reservation } from "shared/reservation"
import { useNotification } from "src/components/NotificationProvider"
import { Sl2Btn } from "src/components/Sl2Btn"
import { BrowserPrintSetupDialog } from "./BrowserPrintSetupDialog"
import { BrowserPrintStatusIcon } from "./BrowserPrintStatusIcon"
import { printErrorMessageKey } from "./printErrors"
import { useDepositCodePrinting } from "./useDepositCodePrinting"

type DepositCodePrintButtonProps = {
  tenantId?: string
  reservation: Reservation
  lockerName?: string
  doorLabel?: string
}

/**
 * LOCKER-522 — "Print reception code" on an active reservation. Renders
 * nothing unless the tenant has `DepositCodePrinting`. While Browser Print is
 * not ready the button opens the setup guide instead of failing silently.
 */
export const DepositCodePrintButton: FC<DepositCodePrintButtonProps> = ({
  tenantId,
  reservation,
  lockerName,
  doorLabel,
}) => {
  const { t } = useTranslation()
  const { showNotification } = useNotification()
  const { isEnabled, health, print } = useDepositCodePrinting(tenantId)
  const [isPrinting, setIsPrinting] = useState(false)
  const [isHelpOpen, setIsHelpOpen] = useState(false)

  if (!isEnabled) return null

  const onPrint = async () => {
    if (health.status !== "ready") {
      setIsHelpOpen(true)
      return
    }
    setIsPrinting(true)
    try {
      const printer = await print({ reservation, lockerName, doorLabel })
      showNotification(
        t("receptionCodePrinted", { name: printer.name || "Zebra" }),
        "success",
      )
    } catch (err) {
      console.error("Reception code print failed", err)
      showNotification(t(printErrorMessageKey(err)), "error")
    } finally {
      setIsPrinting(false)
    }
  }

  return (
    <Stack
      direction="row"
      spacing={0.5}
      alignItems="center"
      justifyContent="center"
      data-ui="UI-SL2W-291"
    >
      <Sl2Btn
        variant="outlined"
        size="small"
        onClick={onPrint}
        disabled={isPrinting}
        startIcon={isPrinting ? <CircularProgress size={14} /> : <PrintIcon />}
        endIcon={<BrowserPrintStatusIcon health={health} size={16} />}
      >
        {t("printReceptionCode")}
      </Sl2Btn>
      <Tooltip title={t("browserPrintSetupTitle")}>
        <IconButton size="small" onClick={() => setIsHelpOpen(true)}>
          <HelpOutlineIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {isHelpOpen && (
        <BrowserPrintSetupDialog
          tenantId={tenantId}
          onClose={() => setIsHelpOpen(false)}
        />
      )}
    </Stack>
  )
}
